import React, { useState, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { UPDATE_CLINIC_PARENT_CONTACT_FROM_PIMS } from 'shared/mutations/updateClinicPetParentContactInfoFromPims';
import { useMutation } from '@apollo/client';
import { ClinicPetParent, SidePanelProfileClinicPetParentFragment } from 'shared/types/graphql';
import { GraphQLErrorPolicies } from 'shared/enums/GraphQLErrorPolicies';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { Button, HStack, Icon } from '@televet/kibble-ui';

enum ContactSyncStateType {
  Start,
  Loading,
  Success,
  Failure,
  PostFailureLoading,
}

interface ISidePanelContactInfoSync {
  clinicPetParent: SidePanelProfileClinicPetParentFragment;
}

export const SidePanelContactInfoSync = ({ clinicPetParent }: ISidePanelContactInfoSync): JSX.Element | null => {
  const { primaryIntegrationName } = useIntegrationsProvider();

  const [updateClinicParentContactInfoFromPims, { loading: dataIsLoading }] = useMutation<{
    updateClinicPetParentContactInfoFromPims: ClinicPetParent;
  }>(UPDATE_CLINIC_PARENT_CONTACT_FROM_PIMS, {
    variables: {
      where: {
        id: clinicPetParent?.id,
      },
    },
    errorPolicy: GraphQLErrorPolicies.All,
  });
  const [contactSyncState, setContactSyncState] = useState(ContactSyncStateType.Start);
  const isError = [ContactSyncStateType.Failure, ContactSyncStateType.PostFailureLoading].includes(contactSyncState);
  const isLoading =
    contactSyncState === ContactSyncStateType.Loading || contactSyncState === ContactSyncStateType.PostFailureLoading;

  const refreshContactData = useCallback(async () => {
    if (!clinicPetParent?.id) return;
    setContactSyncState((state) => {
      const isSuccess = [
        ContactSyncStateType.Start,
        ContactSyncStateType.Success,
        ContactSyncStateType.Loading,
      ].includes(state);
      return isSuccess ? ContactSyncStateType.Loading : ContactSyncStateType.PostFailureLoading;
    });
    try {
      const result = await updateClinicParentContactInfoFromPims();
      if (result.errors) {
        setContactSyncState(ContactSyncStateType.Failure);
        return;
      }
      setContactSyncState(ContactSyncStateType.Success);
    } catch (err) {
      setContactSyncState(ContactSyncStateType.Failure);
    }
  }, [clinicPetParent?.id, updateClinicParentContactInfoFromPims, setContactSyncState]);

  if (!clinicPetParent) return null;
  const { updatedAt, createdAt } = clinicPetParent;
  // Format data
  const time = updatedAt || createdAt;
  const system = primaryIntegrationName;
  const date = time
    ? `Last Updated: ${format(parseISO(time), 'MM/dd/yy')} at ${format(parseISO(time), 'h:mm:ss bbb')}`
    : 'No Sync Data available';

  return (
    <HStack className="PetParentProfile__RefreshContactInfo" justify="space-between" pb={4} px={5} w="100%">
      {!isError ? (
        <>
          <Text size="xs" fontWeight="bold" alignSelf="center">
            {date}
          </Text>
          <Button
            size="xs"
            variant="ghostNeutral"
            isLoading={isLoading}
            loadingText={`Syncing with ${system}`}
            iconName="doubleArrowCircle"
            onClick={refreshContactData}
          >
            Refresh
          </Button>
        </>
      ) : (
        <>
          <HStack>
            <Icon size="xs" name="warningSign" variant="danger" />
            <Text size="xs" fontWeight="bold" alignSelf="center" variant="danger">
              Failed to Update
            </Text>
          </HStack>
          <HStack justify="space-between">
            <Button
              iconName="support"
              variant="ghostNeutral"
              onClick={(): void => {
                if (window.zE) {
                  window.zE('messenger', 'open');
                }
              }}
              size="xs"
            >
              Support
            </Button>
            <Button
              iconName="doubleArrowCircle"
              variant="ghostNeutral"
              onClick={refreshContactData}
              isLoading={dataIsLoading}
              loadingText="Loading"
              size="xs"
            >
              Try Again
            </Button>
          </HStack>
        </>
      )}
    </HStack>
  );
};
